/**
 * LO-FI — live FX plugin counterpart to the Slicer's offline
 * `Apply LO-FI` tool (`lib/loFi.ts`). Registered alongside the
 * other Tone-backed effects so it drops into any bus's FX chain
 * exactly like Chorus / Compressor / FFT Analyzer.
 *
 * v1 scope (this PR): **bit-crush only**, via `Tone.BitCrusher`.
 * Presets S950 / SP-1200 / Amiga / Custom map to the bit-depth
 * axis only. The live sample-rate-reduction axis that the offline
 * tool ships requires an `AudioWorkletProcessor` (no native Web
 * Audio SR reducer exists), and the plugin's `createNode` contract
 * is synchronous — that's a bigger bootstrap change slated as a
 * follow-up. Until then:
 *   - `bitDepth` is fully wired; S950 = 12, SP-1200 / Amiga = 8.
 *   - `sampleRate` is persisted in the preset but only stamps the
 *     display value — does NOT DSP-filter audio. Users wanting
 *     live SR reduction add LO-FI offline via the Slicer panel
 *     (which has always been destructive-into-buffer).
 *
 * Reuse note: pulls preset shapes from the offline `LOFI_PRESETS`
 * so the live + offline paths can never disagree on what S950
 * means.
 */

import { BitCrusher, Gain as ToneGain, type ToneAudioNode } from 'tone'
import { registerEffect } from '@/audio/plugins/registry'
import type { EffectPlugin, PluginHandle } from '@/audio/plugins/types'
import { registerToneContext } from './registerContext'
import { LOFI_PRESETS } from '@/lib/loFi'

const PLUGIN_ID = 'lo-fi'

/** Preset → `[bitDepth, sampleRateHz]` tuples. The `sampleRateHz`
 *  column is persisted but not DSP-applied until the SR-reduction
 *  worklet ships (see module doc). `Custom` leaves the user's
 *  editable values alone. */
const PRESET_OPTIONS = [
  [0, 'Custom'],
  [1, 'S950'],
  [2, 'SP-1200'],
  [3, 'Amiga'],
] as const

const PRESET_BY_INDEX: Record<
  number,
  { bitDepth: number; sampleRateHz: number }
> = {
  1: {
    bitDepth: LOFI_PRESETS.s950.bitDepth,
    sampleRateHz: LOFI_PRESETS.s950.sampleRateHz,
  },
  2: {
    bitDepth: LOFI_PRESETS.sp1200.bitDepth,
    sampleRateHz: LOFI_PRESETS.sp1200.sampleRateHz,
  },
  3: {
    bitDepth: LOFI_PRESETS.amiga.bitDepth,
    sampleRateHz: LOFI_PRESETS.amiga.sampleRateHz,
  },
}

const BIT_DEPTH_OPTIONS: ReadonlyArray<[number, string]> = [
  [4, '4-bit'],
  [8, '8-bit'],
  [12, '12-bit'],
  [16, '16-bit · bypass'],
]

const SAMPLE_RATE_OPTIONS: ReadonlyArray<[number, string]> = [
  [22050, '22 k'],
  [26000, '26 k'],
  [32000, '32 k'],
  [48000, 'native'],
]

let registered = false

export function registerLoFiEffect(): void {
  if (registered) return
  registered = true
  registerEffect(loFiPlugin)
}

export const loFiPlugin: EffectPlugin = {
  id: PLUGIN_ID,
  label: 'LO-FI',
  category: 'distortion',
  defaultPreset: {
    preset: 0,
    bitDepth: 12,
    sampleRate: 48000,
    wet: 1,
  },
  paramSchema: [
    {
      id: 'preset',
      label: 'Preset',
      kind: 'select',
      default: 0,
      options: PRESET_OPTIONS.map(([v, label]) => [v, label]),
      help: 'Snapshot of the classic-sampler flavours. Picking one stamps bit depth (and, once the live SR-reduction worklet ships, sample rate). Custom leaves the individual axes editable.',
    },
    {
      id: 'bitDepth',
      label: 'Bit depth',
      kind: 'select',
      default: 12,
      options: BIT_DEPTH_OPTIONS,
    },
    {
      id: 'sampleRate',
      label: 'Sample rate',
      kind: 'select',
      default: 48000,
      options: SAMPLE_RATE_OPTIONS,
      help: 'Persisted, but not DSP-applied live in this PR — see the Slicer LO-FI panel for offline SR reduction.',
    },
    {
      id: 'wet',
      label: 'Wet',
      kind: 'slider',
      min: 0,
      max: 1,
      step: 0.01,
      default: 1,
    },
  ],

  createNode(ctx, preset = {}): PluginHandle {
    registerToneContext(ctx)
    const crusher = new BitCrusher(clampBitDepth(preset.bitDepth ?? 12))
    crusher.wet.value = preset.wet ?? 1
    return wrapToneEffect(crusher, {
      preset: value => {
        const mapped = PRESET_BY_INDEX[Math.round(value)]
        if (!mapped) return
        crusher.bits.value = clampBitDepth(mapped.bitDepth)
        // `sampleRate` axis writes fall through the setParam branch
        // below and are persisted on the BusEffect params for when
        // the worklet lands.
      },
      bitDepth: value => {
        crusher.bits.value = clampBitDepth(value)
      },
      sampleRate: () => {
        /* v1: view-only; persisted on BusEffect.params so the
           follow-up SR-reduction worklet sees the desired target. */
      },
      wet: value => {
        crusher.wet.value = Math.max(0, Math.min(1, value))
      },
    })
  },
}

function clampBitDepth(raw: number): number {
  const n = Math.round(raw)
  if (n <= 4) return 4
  if (n >= 16) return 16
  return n
}

/** Same wrapper as `toneEffects.ts` — Tone nodes exit as plain
 *  AudioNodes for the PluginHandle shape, and dispose tears down
 *  the Tone-owned graph so no audio keeps running after the bus
 *  removes the slot. Duplicated here because `toneEffects.ts`
 *  doesn't export it; if that changes, inline calls migrate to
 *  the shared helper. */
function wrapToneEffect(
  node: ToneAudioNode,
  setters: Record<string, (value: number) => void>,
): PluginHandle {
  const input = new ToneGain({ context: node.context })
  const output = new ToneGain({ context: node.context })
  input.connect(node)
  node.connect(output)
  return {
    input: input.output as unknown as AudioNode,
    output: output.output as unknown as AudioNode,
    setParam(name, value) {
      const setter = setters[name]
      if (setter) setter(value)
    },
    dispose() {
      try {
        input.dispose()
        node.dispose()
        output.dispose()
      } catch {
        /* already disposed */
      }
    },
  }
}
